import { useMemo } from 'react';

type UiSchema =
  | { type: 'VerticalLayout'; elements: UiSchema[] }
  | { type: 'HorizontalLayout'; elements: UiSchema[] }
  | { type: 'Group'; label?: string; elements: UiSchema[] }
  | { type: 'Control'; scope: string; label?: string; options?: Record<string, any> };

const COLS_PER_ROW = 3;

/** Chunk elements into rows of COLS_PER_ROW and wrap each row in a HorizontalLayout */
function chunkIntoHorizontalRows(elements: UiSchema[], cols: number): UiSchema[] {
  if (elements.length === 0) return [];
  const rows: UiSchema[] = [];
  for (let i = 0; i < elements.length; i += cols) {
    const chunk = elements.slice(i, i + cols);
    rows.push({ type: 'HorizontalLayout', elements: chunk });
  }
  return rows;
}

/** True if the element is a Control (leaf field) */
function isControl(
  el: UiSchema
): el is { type: 'Control'; scope: string; label?: string; options?: Record<string, any> } {
  return el.type === 'Control';
}

/**
 * At root: chunk only consecutive Controls into HorizontalLayouts.
 * Groups are emitted as‑is so each gets its own full‑width row.
 */
function chunkRootElements(elements: UiSchema[], cols: number): UiSchema[] {
  const result: UiSchema[] = [];
  let controlBuffer: UiSchema[] = [];

  const flushControls = () => {
    if (controlBuffer.length === 0) return;
    result.push(...chunkIntoHorizontalRows(controlBuffer, cols));
    controlBuffer = [];
  };

  for (const el of elements) {
    if (isControl(el)) {
      controlBuffer.push(el);
    } else {
      flushControls();
      result.push(el); // Group → own row
    }
  }
  flushControls();
  return result;
}

/** Recursively build layout elements from schema properties */
const buildControls = (
  properties: Record<string, any>,
  parentScope = '#/properties'
): UiSchema[] => {
  return Object.entries(properties).map(([key, prop]) => {
    const scope = `${parentScope}/${key}`;
    const label = prop.title || key;

    // oneOf / anyOf (e.g. raw_config) → its own full‑width section
    if (
      (Array.isArray((prop as any).oneOf) && (prop as any).oneOf.length > 0) ||
      (Array.isArray((prop as any).anyOf) && (prop as any).anyOf.length > 0)
    ) {
      return {
        type: 'Group',
        label,
        elements: [
          {
            type: 'VerticalLayout',
            elements: [
              {
                type: 'Control',
                scope,
                label,
              },
            ],
          },
        ],
      };
    }

    // Nested object-like schema (has properties, possibly via allOf) → full-width section with its own vertical stack.
    // Some schemas (e.g. Exchange / Hedger) wrap the actual object shape inside allOf, so detect that as well.
    let objectSchema: any | undefined;
    if (prop && typeof prop === 'object') {
      if ((prop as any).properties && typeof (prop as any).properties === 'object') {
        objectSchema = prop;
      } else if (Array.isArray((prop as any).allOf)) {
        objectSchema = (prop as any).allOf.find(
          (sub: any) => sub && typeof sub === 'object' && sub.properties && typeof sub.properties === 'object'
        );
      }
    }

    if (objectSchema) {
      const nestedElements = buildControls(objectSchema.properties, `${scope}/properties`);
      return {
        type: 'Group',
        label,
        elements: [
          {
            type: 'VerticalLayout',
            elements: nestedElements,
          },
        ],
      };
    }

    // Default → simple Control (participates in 3‑column root grid)
    return {
      type: 'Control',
      scope,
      label,
    };
  });
};

export const useUiSchemaGenerator = (processedSchema: any) => {
  return useMemo(() => {
    if (!processedSchema?.properties) {
      return { type: 'VerticalLayout', elements: [] };
    }

    const topLevelElements = buildControls(processedSchema.properties);

    // Root: 3‑column grid for plain Controls, Groups as their own full‑width rows
    return {
      type: 'VerticalLayout',
      elements: chunkRootElements(topLevelElements, COLS_PER_ROW),
    };
  }, [processedSchema]);
};